// client/death.ts — Traduction PURE et testable de la cause de mort d'un coureur.
// La cause vit DÉJÀ dans l'event moteur `death` (shared/state.ts, INCHANGÉ) : ce module
// ne fait que la lire. Aucun effet de bord, aucun accès DOM au chargement -> importable
// tel quel dans les tests (contrairement aux modules qui touchent le canvas / Web Audio).
//   - causeLabel   : cause -> libellé français pour l'écran de perte solo (« PERDU ! »).
//   - deathSound   : cause -> courte séquence sonore DISTINCTE (jouée par seq() dans playEvents).
//   - lastDeathCause : cause du dernier `death` d'un coureur dans un lot d'events.
import type { GameEvent } from '../shared/state'

// Dérivée de l'event moteur : reste automatiquement en phase si une cause est ajoutée
// côté shared (aucune duplication de la liste, aucun `shared/` modifié).
export type DeathCause = Extract<GameEvent, { type: 'death' }>['cause']

// Libellé affiché sur l'écran de perte solo. Les 5 causes + un défaut sûr : si une 6e
// cause apparaissait côté moteur, on affiche « Aïe ! » plutôt qu'un undefined.
export function causeLabel(cause: DeathCause | null | undefined): string {
  switch (cause) {
    case 'monk': return 'Attrapé !'
    case 'buried': return 'Écrasé !'
    case 'bomb': return 'Boum !'
    case 'fell': return 'Tombé !'
    case 'lava': return 'Brûlé !'
    case 'deathzone': return 'Zone mortelle !'
    case 'crushed': return 'Aplati !' // écrasé par une caisse (Phase 4) ; « Écrasé ! » est pris par 'buried'
    case 'drowned': return 'Glouglou !' // à bout de souffle sous l'eau (EAU 16/07)
    case 'fire': return 'Grillé !'   // FEU (16/07) : mur de flammes
    case 'spikes': return 'Piqué !'  // PICS (16/07) : tombé sur les pointes
    case 'fish': return 'Croqué !'   // PIRANHA (16/07 soir) : happé dans l'eau
    default: return 'Aïe !'
  }
}

// Note d'un court jingle. Structurellement compatible avec le type `Note` de audio.ts
// (consommé par seq()) : death.ts n'importe PAS audio.ts, ce qui évite tout cycle.
export interface DeathNote { f: number; ms: number; at: number; type?: OscillatorType; to?: number; vol?: number }

// Profil sonore par cause (PURE) : une courte séquence NETTEMENT différenciée (hauteur,
// forme d'onde, sens), jouée par seq() dans playEvents. Sobre : 1-2 notes, faible volume,
// pas de montée agressive. Défaut sûr = le jingle de mort historique (descente 440 -> 110).
export function deathSound(cause: DeathCause | null | undefined): DeathNote[] {
  switch (cause) {
    // Attrapé par un moine : happe descendant menaçant (deux notes qui plongent).
    case 'monk': return [
      { f: 420, ms: 90, at: 0, type: 'sawtooth', to: 220, vol: 0.18 },
      { f: 220, ms: 150, at: 0.09, type: 'sawtooth', to: 110, vol: 0.18 },
    ]
    // Écrasé par une brique qui repousse : gros « thud » très grave et bref.
    case 'buried': return [
      { f: 150, ms: 220, at: 0, type: 'square', to: 60, vol: 0.2 },
    ]
    // Boum (bombe) : détente très grave et rapide, deux impacts.
    case 'bomb': return [
      { f: 110, ms: 120, at: 0, type: 'sawtooth', to: 50, vol: 0.22 },
      { f: 70, ms: 200, at: 0.1, type: 'sawtooth', to: 40, vol: 0.22 },
    ]
    // Tombé dans le vide : sifflement descendant long (glissando de chute).
    case 'fell': return [
      { f: 700, ms: 320, at: 0, type: 'sine', to: 120, vol: 0.16 },
    ]
    // Brûlé (lave) : deux notes aiguës qui grésillent puis retombent.
    case 'lava': return [
      { f: 900, ms: 80, at: 0, type: 'square', to: 500, vol: 0.14 },
      { f: 500, ms: 180, at: 0.08, type: 'sawtooth', to: 130, vol: 0.16 },
    ]
    // Feu (16/07) : crépitement grave qui monte puis s'étouffe (proche lave, plus sec).
    case 'fire': return [
      { f: 700, ms: 70, at: 0, type: 'square', to: 950, vol: 0.13 },
      { f: 420, ms: 160, at: 0.07, type: 'sawtooth', to: 120, vol: 0.15 },
    ]
    // Pics (16/07) : piqûre sèche descendante (deux clics aigus très brefs).
    case 'spikes': return [
      { f: 1500, ms: 45, at: 0, type: 'square', to: 900, vol: 0.13 },
      { f: 900, ms: 70, at: 0.05, type: 'triangle', to: 250, vol: 0.13 },
    ]
    // Zone mortelle (#1) : zap électrique bref et strident (deux impulsions carrées aiguës).
    case 'deathzone': return [
      { f: 1200, ms: 60, at: 0, type: 'square', to: 300, vol: 0.14 },
      { f: 600, ms: 120, at: 0.06, type: 'square', to: 90, vol: 0.15 },
    ]
    // Aplati par une caisse (Phase 4) : double « clonk » de bois grave (choc de caisse).
    case 'crushed': return [
      { f: 200, ms: 90, at: 0, type: 'square', to: 80, vol: 0.2 },
      { f: 90, ms: 160, at: 0.08, type: 'square', to: 45, vol: 0.2 },
    ]
    // Croqué par un piranha (16/07 soir) : claquement de mâchoire sec + grognement bref.
    case 'fish': return [
      { f: 900, ms: 40, at: 0, type: 'square', to: 300, vol: 0.22 },
      { f: 160, ms: 180, at: 0.05, type: 'sawtooth', to: 60, vol: 0.18 },
    ]
    // Noyade (EAU 16/07) : deux glouglous étouffés, une bulle qui remonte puis tout s'éteint.
    case 'drowned': return [
      { f: 300, ms: 140, at: 0, type: 'sine', to: 430, vol: 0.15 },
      { f: 240, ms: 240, at: 0.14, type: 'sine', to: 90, vol: 0.16 },
    ]
    // Défaut sûr : jingle de mort historique.
    default: return [
      { f: 440, ms: 400, at: 0, type: 'sawtooth', to: 110, vol: 0.2 },
    ]
  }
}

// Cause du DERNIER `death` du coureur `who` dans un lot d'events (null si aucun). Le
// contrôleur solo s'en sert pour mémoriser POURQUOI on est mort et l'afficher. Solo n'a
// qu'un coureur (index 0), mais le filtre par index garde la fonction correcte en général.
export function lastDeathCause(events: readonly GameEvent[], who: number): DeathCause | null {
  let cause: DeathCause | null = null
  for (const e of events) if (e.type === 'death' && e.runner === who) cause = e.cause
  return cause
}
